import { AVATAR_COLORS } from './constants/ui';

/**
 * ID 기반으로 일관된 아바타 색상을 반환하는 함수
 *
 * @description
 * 동일한 ID에 대해 항상 동일한 색상을 반환하여 사용자 식별을 용이하게 합니다.
 * 해시 함수를 사용하여 색상 배열 인덱스를 계산합니다.
 *
 * @param id - 사용자 또는 엔티티의 고유 ID
 * @returns AVATAR_COLORS 배열의 색상 중 하나 (CSS 변수)
 *
 * @example
 * ```typescript
 * const color = getAvatarColor('user-123'); // 'var(--opds-glo-palette-blue-70)'
 * ```
 */
export const getAvatarColor = (id: string): string => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

/**
 * 이름에서 이니셜을 추출하는 함수
 *
 * @description
 * 단일 단어인 경우 첫 글자만, 여러 단어인 경우 첫 단어와 마지막 단어의 첫 글자를 반환합니다.
 *
 * @param name - 사용자 이름
 * @returns 대문자로 변환된 이니셜 (1~2글자)
 *
 * @example
 * ```typescript
 * getInitials('John Doe') // 'JD'
 * getInitials('Alice') // 'A'
 * ```
 */
export const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};
